import ExportButton from '../../../components/ExportButton';
import { cloneElement, isValidElement, useEffect, useState } from 'react';
import { RiDeleteBinLine } from 'react-icons/ri';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from "react-router-dom";
import Breadcrumbs from "../../../components/Breadcrumbs";
import Button from "../../../components/button";
import CustomModal from '../../../components/CustomModal';
import MultiLineText from '../../../components/MultiLineText';
import NewTableComponent from '../../../components/NewTableComponent';
import PermissionWrapper from '../../../components/PermissionWrapper';
import Stats from "../../../components/Stats";
import { fetchFuelCompaniesDropdown } from '../../../store/Slices/dropdownSlice';
import { deleteFuelStation, fetchFuelStations } from '../../../store/Slices/FuelStationSlice';
import '../../../styles/TransportersTable.css';
import '../../../styles/Services.css';
import { AppMainModules, FuelStationPermission, FuelSubModules } from '../../../utils/app-constant';
import { activeStatsIcon, lateStatsIcon, plusIcon, successIcon, tripStatsIcon, upcomingInspection, wrongIcon } from "../../../utils/svgs";
import { errorNotify, successNotify } from '../../../utils/toast';
import { useFuelStationListingExport } from './useFuelStationListingExport';

const formatFuelRate = (rate) => {
    const price = rate?.price;
    if (price === undefined || price === null || price === '') return '-';
    return `Rs ${price}`;
};

const renderStatusIcon = (icon) => {
    if (!isValidElement(icon)) {
        return icon;
    }

    const normalizedSize = 18;

    return (
        <span
            style={{
                width: "18px",
                height: "18px",
                minWidth: "18px",
                minHeight: "18px",
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
            }}
        >
            {cloneElement(icon, {
                width: normalizedSize,
                height: normalizedSize,
                style: {
                    width: "18px",
                    height: "18px",
                    minWidth: "18px",
                    minHeight: "18px",
                    display: "block",
                    ...(icon.props?.style || {}),
                },
            })}
        </span>
    );
};

const FuelStations = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch();

    const { getFuelStationsPaginated, getFuelStationsLoading, deleteFuelStationLoading } = useSelector((state) => state.fuelStation);
    const { fuelCompaniesDropdownData } = useSelector((state) => state.dropdowns);

    const [currentPage, setCurrentPage] = useState(1);
    const [limit, setLimit] = useState(10);
    const [searchText, setSearchText] = useState('');
    const [deleteStationPopup, setDeleteStationPopup] = useState(false);
    const [deleteStationId, setDeleteStationId] = useState(null);
    const [searchFilters, setSearchFilters] = useState(null);

    const {
        hasActiveFilters,
        exportLoading,
        pdfExportLoading,
        excelExportLoading,
        handleExportPdf,
        handleExportExcel,
    } = useFuelStationListingExport(searchText, searchFilters);

    useEffect(() => {
        dispatch(fetchFuelCompaniesDropdown());
    }, [dispatch]);

    useEffect(() => {
        const params = {
            page: currentPage,
            limit: limit,
            search_text: searchText,
            searchFilters: searchFilters
        };
        dispatch(fetchFuelStations(params));
    }, [dispatch, searchText, currentPage, limit, searchFilters]);

    const handleDeleteStation = async () => {
        try {
            await dispatch(deleteFuelStation(deleteStationId)).unwrap();
            successNotify('Fuel station deleted successfully');
            setDeleteStationPopup(false);
            setDeleteStationId(null);
            dispatch(fetchFuelStations({ page: currentPage, limit: limit, searchFilters }));
        } catch (error) {
            errorNotify(error);
        }
    };

    const commonColumns = {
        checked: true,
        disabled: true,
        hide: false,
        exportColumn: true,
        sortable: false,
    };

    const columns = [
        {
            ...commonColumns,
            name: "ID",
            key: "fuel_station_id",
            selector: (row) => (
                <span
                    className="hyperlink"
                    onClick={() => navigate(`/fuel-stations/details/${row?.id}`)}
                    style={{ cursor: 'pointer' }}
                >
                    {`${row?.fuel_station_id ?? `FS-${row?.id}`}`}
                </span>
            ),
            tableColumn: false,
        },
        {
            ...commonColumns,
            name: "Station Name",
            key: "name",
            selector: (row) => <MultiLineText text={row?.name || "-"} maxWidth="180px" />,
            tableColumn: false,
            width: "220px"
        },
        {
            ...commonColumns,
            name: "Contact",
            key: "contact",
            selector: (row) => row?.contact || "-",
            tableColumn: false,
            width: "170px"
        },
        {
            ...commonColumns,
            name: "Location",
            key: "location",
            selector: (row) => <MultiLineText text={row?.location || "-"} maxWidth="150px" />,
            tableColumn: false,
            width: "250px"
        },
        {
            ...commonColumns,
            name: "Company",
            key: "fuel_company_id",
            selector: (row) => {
                const company = fuelCompaniesDropdownData.find(c => c.id === row.fuel_company_id);
                const companyName = company ? company.name : (row.fuel_company_id || "-");
                return <MultiLineText text={companyName} maxWidth="150px" />;
            },
            tableColumn: false,
            width: "250px",
        },
        {
            ...commonColumns,
            name: "Status",
            key: "status",
            selector: (row) => {
                const status = String(row?.status || "").toLowerCase().trim();
                const isActive = status === "active";
                const color = isActive ? "#299900" : "#EF0000";
                const icon = isActive ? successIcon : wrongIcon;
                const label = isActive ? "Active" : "Inactive";
                return (
                    <div className="asset_status_cell" style={{ color }}>
                        {renderStatusIcon(icon)}
                        {label}
                    </div>
                );
            },
            tableColumn: true,
            filterName: "statusFilter",
            type: "select",
            options: [
                { label: "Active", value: "active" },
                { label: "Inactive", value: "inactive" }
            ],
            width: "150px"
        },
        {
            ...commonColumns,
            name: "Petrol Price",
            key: "petrol_current_rate",
            selector: (row) => formatFuelRate(row?.petrol_current_rate),
            tableColumn: false,
            width: "150px"
        },
        {
            ...commonColumns,
            name: "Diesel Price",
            key: "diesel_current_rate",
            selector: (row) => formatFuelRate(row?.diesel_current_rate),
            tableColumn: false,
            width: "150px"
        },
        {
            ...commonColumns,
            name: "High Octane",
            key: "high_octane_current_rate",
            selector: (row) => formatFuelRate(row?.high_octane_current_rate),
            tableColumn: false,
            width: "150px"
        },
        {
            name: "Actions",
            key: "actions",
            checked: true,
            disabled: false,
            hide: false,
            exportColumn: false,
            selector: (row) => (

                <PermissionWrapper
                    module={FuelSubModules.FuelStations}
                    permission={FuelStationPermission.Delete}
                    mainModule={AppMainModules.fuelManagement}
                >
                    <div className="table_action_btn">
                        <button onClick={() => {
                            setDeleteStationPopup(true);
                            setDeleteStationId(row?.id);
                        }} className="delete_btn">
                            <RiDeleteBinLine />
                        </button>
                    </div>
                </PermissionWrapper>
            ),
            sortable: false,
            tableColumn: false
        }
    ];

    const fuelStationStatsData = [
        {
            title: "Total Stations",
            value: getFuelStationsPaginated?.stats?.total_stations?.toString() || getFuelStationsPaginated?.meta?.totalItems?.toString() || "0",
            text: (
                <>
                    <span>Total fuel stations registered</span>
                </>
            ),
            icon: tripStatsIcon,
        },
        {
            title: "Active Stations",
            value: getFuelStationsPaginated?.stats?.active_stations?.toString() || "0",
            text: (
                <>
                    <span>Currently operational</span>
                </>
            ),
            icon: activeStatsIcon,
        },
        {
            title: "Inactive Stations",
            value: getFuelStationsPaginated?.stats?.inactive_stations?.toString() || "0",
            text: (
                <>
                    <span>Currently offline/closed</span>
                </>
            ),
            icon: lateStatsIcon,
        },
        {
            title: "Expiring Contracts",
            value: getFuelStationsPaginated?.stats?.contracts_expiring_30_days?.toString() || "0",
            text: (
                <>
                    <span>Expiring within 30 days</span>
                </>
            ),
            icon: upcomingInspection,
        },
    ];

    const processedResults = (getFuelStationsPaginated?.results || []).map((item, idx, arr) => {
        const isLastFour = idx >= arr.length - 4;
        return { ...item, isLastFourServiceRow: isLastFour };
    });

    return (
        <>
            <Breadcrumbs values={['Fuel Management', 'Fuel Stations']} />

            <CustomModal show={deleteStationPopup} handleClose={() => setDeleteStationPopup(false)} size="md" heading="Delete Fuel Station" customHeaderCss="custom_header_css">
                <div className="delete_user_modal_body">
                    <p>Are you sure you want to delete this fuel station?</p>
                    <div className='d-flex align-items-center gap-2'>
                        <Button
                            cancelBtn={() => setDeleteStationPopup(false)}
                            submitBtn={handleDeleteStation}
                            loading={deleteFuelStationLoading}
                            disabled={deleteFuelStationLoading}
                            buttonExtraCss="delete_user_modal_btn"
                            submitText="Delete"
                            type="submit"
                            shouldRow={false}
                        />
                    </div>
                </div>
            </CustomModal>

            <div className='page_header access_view'>
                <h2>Fuel Stations Dashboard</h2>
                <div className="services_buttons">
                    <ExportButton
                        type="pdf"
                        onClick={handleExportPdf}
                        disabled={!hasActiveFilters || pdfExportLoading}
                        loading={pdfExportLoading}
                        hasActiveFilters={hasActiveFilters}
                    />

                    <ExportButton
                        type="excel"
                        onClick={handleExportExcel}
                        disabled={!hasActiveFilters || excelExportLoading}
                        loading={excelExportLoading}
                        hasActiveFilters={hasActiveFilters}
                    />

                    <PermissionWrapper
                        module={FuelSubModules.FuelStations}
                        permission={FuelStationPermission.Create}
                        mainModule={AppMainModules.fuelManagement}
                    >
                        <Button
                            iconButton={true}
                            type="button"
                            iconBtnText="Add Fuel Station"
                            icon={plusIcon}
                            shouldRow={false}
                            isShowCancel={false}
                            isShowSubmitBtn={false}
                            onClick={() => navigate('/fuel-stations/create')}
                        />
                    </PermissionWrapper>
                </div>
            </div>

            <Stats statsData={fuelStationStatsData} />

            <NewTableComponent
                showAddBtn={false}
                columns={columns}
                data={processedResults}
                currentPage={currentPage}
                setCurrentPage={setCurrentPage}
                limit={limit}
                setLimit={setLimit}
                totalItems={getFuelStationsPaginated?.meta?.totalItems}
                totalPages={getFuelStationsPaginated?.meta?.totalPages}
                onSearch={(text) => { setSearchText(text); setCurrentPage(1); }}
                searchValue={searchText}
                loading={getFuelStationsLoading}
                paginationLoading={getFuelStationsLoading}
                setSearchFilters={setSearchFilters}
                searchFilters={searchFilters}
                tableClassName="transporters_table"
            />
        </>
    );
};

export default FuelStations;
